import { useState, useEffect } from "react";
import { useDocHubDialog, TipDialogData } from "@/contexts/DocHubDialogContext";
import { useBrandSelection } from "@/hooks/useBrandSelection";
import { useDocHubTips } from "@/hooks/useDocHubTips";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { TipsTagSelector } from "@/components/documentation-hub/TipsTagSelector";

interface DocHubCategory {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  color?: string | null;
}

export const TipDialog = () => {
  const { currentBrandId } = useBrandSelection();
  const { dialogType, isOpen, tipData, closeDialog, onSuccess } = useDocHubDialog();
  const { createTip, updateTip } = useDocHubTips(currentBrandId || '');
  
  const [formData, setFormData] = useState<TipDialogData>({
    name: '',
    content: '',
    category_id: null,
    tags: [],
    attachments: [],
  });
  const [isUploading, setIsUploading] = useState(false);

  const isEditing = !!tipData?.id;
  const dialogOpen = dialogType === 'tip' && isOpen;

  // Fetch categories
  const { data: categories = [] } = useQuery<DocHubCategory[]>({
    queryKey: ['doc-hub-tips-categories', currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips_categories')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('name');
      if (error) throw error;
      return data as DocHubCategory[];
    },
    enabled: !!currentBrandId && dialogOpen,
  });

  // Sync form data when dialog opens
  useEffect(() => {
    if (tipData && dialogOpen) {
      setFormData({
        id: tipData.id,
        name: tipData.name || '',
        content: tipData.content || '',
        category_id: tipData.category_id || null,
        tags: tipData.tags || [],
        attachments: tipData.attachments || [],
      });
    }
  }, [tipData, dialogOpen]);

  const handleFileUpload = async (files: File[]) => {
    if (!currentBrandId) return;
    
    setIsUploading(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${currentBrandId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('os_doc_hub_tips')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('os_doc_hub_tips')
          .getPublicUrl(filePath);

        uploadedUrls.push(data.publicUrl);
      }

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedUrls]
      }));
      
      toast.success('Files uploaded successfully');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
  });

  const handleSubmit = async () => {
    if (!currentBrandId || !formData.name.trim() || !formData.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      toast.error('You must be logged in');
      return;
    }

    if (isEditing && formData.id) {
      updateTip(formData.id, {
        brand_id: currentBrandId,
        user_id: user.id,
        name: formData.name,
        content: formData.content,
        category_id: formData.category_id,
        tags: formData.tags,
        attachments: formData.attachments,
      });
    } else {
      createTip({
        brand_id: currentBrandId,
        user_id: user.id,
        name: formData.name,
        content: formData.content,
        category_id: formData.category_id,
        tags: formData.tags,
        attachments: formData.attachments,
      });
    }

    onSuccess?.();
    closeDialog();
    setFormData({ name: '', content: '', category_id: null, tags: [], attachments: [] });
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      closeDialog();
      setFormData({ name: '', content: '', category_id: null, tags: [], attachments: [] });
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleClose} modal={false}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isEditing ? "Edit Tip" : "Add New Tip"} 
            popupContext="/documentation-hub/tips" 
            isOpen={dialogOpen}
          >
            {isEditing ? 'Edit Tip' : 'Add New Tip'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Tip Title *</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              placeholder="Enter tip title" 
            />
          </div>
          <div>
            <Label htmlFor="category">Category</Label>
            <Select 
              value={formData.category_id || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? null : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="none">No Category</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <TipsTagSelector
              brandId={currentBrandId || ''}
              selectedTagIds={formData.tags}
              onTagsChange={(tagIds) => setFormData({ ...formData, tags: tagIds })}
            />
          </div>
          <div>
            <Label htmlFor="content">Tip Content *</Label>
            <Textarea 
              id="content" 
              value={formData.content} 
              onChange={(e) => setFormData({ ...formData, content: e.target.value })} 
              placeholder="Enter tip content" 
              rows={6} 
              className="resize-none" 
            />
          </div>
          <div>
            <Label>Attachments</Label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
              </p>
            </div>
            {formData.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {formData.attachments.map((url, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded">
                    <span className="text-sm truncate flex-1">{url.split('/').pop()}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeAttachment(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isUploading}>
              {isEditing ? 'Update' : 'Create'} Tip
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
